import { type FormEvent, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import {
  forgetPin,
  type PrivateNote as Note,
  rememberedPin,
  rememberPin,
  useOpenPrivateNote,
  usePrivateNoteStatus,
  useSavePrivateNote,
  useSetNotePin,
} from '../api/privateNote';
import { dt } from '../lib/format';
import { useUi } from '../state/ui';
import { I } from './Icons';
import { Btn, Field, H2, Surface, TextareaField } from './ui';

/**
 * Interní poznámka dokladu pod zámkem (PIN firmy). Na doklad se netiskne, do exportů
 * ani k asistentovi se nedostane; text pošle server až po ověření PINu. Odemčený PIN
 * si appka pamatuje 10 minut jen v paměti, „Zamknout“ ho zapomene hned.
 */
export function PrivateNote({
  documentId,
  companyId,
  canWrite,
  canSetPin,
}: {
  documentId: string;
  companyId: string;
  canWrite: boolean;
  canSetPin: boolean;
}) {
  const { toast } = useUi();
  const { data: status, error: statusError } = usePrivateNoteStatus(documentId);
  const open = useOpenPrivateNote(documentId);
  const save = useSavePrivateNote(documentId);
  const setPin = useSetNotePin(companyId);
  const [note, setNote] = useState<Note | null>(null);
  const [pin, setPinValue] = useState('');
  const [pin2, setPin2] = useState('');
  const [draft, setDraft] = useState<string | null>(null);

  const unlock = (value: string) =>
    open.mutate(value, {
      onSuccess: (n) => {
        rememberPin(companyId, value);
        setNote(n);
        setPinValue('');
      },
      onError: () => forgetPin(companyId),
    });

  // PIN odemčený u jiné faktury téže firmy (posledních 10 minut) otevře i tuhle; stav
  // se při přechodu na jiný doklad nuluje tím, že ho rodič kreslí s `key` dokladu
  // biome-ignore lint/correctness/useExhaustiveDependencies: jen když firma PIN dostane
  useEffect(() => {
    const remembered = rememberedPin(companyId);
    if (remembered && status?.pinSet && !note && !open.isPending) unlock(remembered);
  }, [status?.pinSet]);

  const lock = () => {
    forgetPin(companyId);
    setNote(null);
    setDraft(null);
  };

  const err =
    open.error instanceof ApiError
      ? open.error.message
      : save.error instanceof ApiError
        ? save.error.message
        : setPin.error instanceof ApiError
          ? setPin.error.message
          : null;

  const head = (
    <H2>
      <span className="pn-head">
        {I.lock}
        Interní poznámka
      </span>
    </H2>
  );

  if (statusError || !status) return null;

  if (!status.pinSet) {
    const submit = (e: FormEvent) => {
      e.preventDefault();
      setPin.mutate(
        { pin: pin.trim() },
        {
          onSuccess: () => {
            rememberPin(companyId, pin.trim());
            toast('PIN nastaven, poznámky jsou pod zámkem');
            setPinValue('');
            setPin2('');
          },
        },
      );
    };
    return (
      <>
        {head}
        <Surface className="pad private-note">
          <p className="muted pn-intro">
            Poznámka jen pro vás, schovaná pod zámkem. Na doklad se netiskne. Nejdřív nastav PIN
            firmy, pak platí pro všechny její doklady.
          </p>
          {canSetPin ? (
            <form className="pn-set" onSubmit={submit}>
              <div className="two">
                <Field
                  id="pn-new"
                  label="Nový PIN (4 až 8 číslic)"
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  value={pin}
                  onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ''))}
                  maxLength={8}
                />
                <Field
                  id="pn-new2"
                  label="PIN znovu"
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  value={pin2}
                  onChange={(e) => setPin2(e.target.value.replace(/\D/g, ''))}
                  maxLength={8}
                  error={pin2 && pin2 !== pin ? 'PIN se neshoduje' : undefined}
                />
              </div>
              {err && <p className="form-error">{err}</p>}
              <Btn
                type="submit"
                sm
                variant="primary"
                loading={setPin.isPending}
                disabled={pin.length < 4 || pin !== pin2}
              >
                Nastavit PIN
              </Btn>
            </form>
          ) : (
            <p className="muted">PIN nastaví správce firmy.</p>
          )}
        </Surface>
      </>
    );
  }

  if (!note) {
    const submit = (e: FormEvent) => {
      e.preventDefault();
      if (pin.trim()) unlock(pin.trim());
    };
    return (
      <>
        {head}
        <Surface className="pad private-note">
          <form className="pn-lock" onSubmit={submit}>
            <div className="grow">
              <div className="l1">Pod zámkem</div>
              <div className="l2">
                <span>
                  {status.exists
                    ? `Je tu poznámka${status.updatedAt ? ` · ${dt(status.updatedAt)}` : ''}`
                    : 'Zatím bez poznámky'}
                </span>
              </div>
            </div>
            <Field
              id="pn-pin"
              label="PIN"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              className="pn-pin"
              value={pin}
              onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ''))}
              maxLength={8}
            />
            <Btn type="submit" sm variant="primary" loading={open.isPending} disabled={!pin}>
              Odemknout
            </Btn>
          </form>
          {err && <p className="form-error">{err}</p>}
        </Surface>
      </>
    );
  }

  const remembered = rememberedPin(companyId);
  return (
    <>
      {head}
      <Surface className="pad private-note">
        {draft === null ? (
          <>
            <div className="pn-text">
              {note.text || <span className="muted">Zatím žádná poznámka.</span>}
            </div>
            <div className="pn-actions">
              {canWrite && (
                <Btn sm onClick={() => setDraft(note.text)}>
                  {note.text ? 'Upravit' : 'Přidat poznámku'}
                </Btn>
              )}
              <Btn sm onClick={lock}>
                Zamknout
              </Btn>
            </div>
          </>
        ) : (
          <>
            <TextareaField
              id="pn-text"
              label="Interní poznámka"
              rows={4}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            {err && <p className="form-error">{err}</p>}
            <div className="two">
              <Btn
                sm
                variant="primary"
                loading={save.isPending}
                onClick={() => {
                  // PIN vypršel (10 minut) mezi otevřením a uložením: zamknout a zadat znovu
                  if (!remembered) {
                    lock();
                    toast('Zámek vypršel, zadej PIN znovu');
                    return;
                  }
                  save.mutate(
                    { pin: remembered, text: draft },
                    {
                      onSuccess: (n) => {
                        rememberPin(companyId, remembered);
                        setNote(n);
                        setDraft(null);
                        toast(n.text ? 'Poznámka uložena' : 'Poznámka smazána');
                      },
                    },
                  );
                }}
              >
                Uložit
              </Btn>
              <Btn sm onClick={() => setDraft(null)}>
                Zrušit
              </Btn>
            </div>
          </>
        )}
      </Surface>
    </>
  );
}
